import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import cloneDeep from 'lodash.clonedeep';
// import {
//   DialedDigitExtractionServerPrivileges,
// } from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.types';
import { ProcessOption, ProcessOptionPrivileges } from 'data/processOption/processOption.types';
import * as actions from 'data/processOption/processOption.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/processOption/processOption.selectors';
import ModalDialog from 'shared/modal/dialog';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/ProcessOptionsGrid';
import { ProcessMenuActionsType, processColumnDefs } from '../../ProcessOptions.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getProcessOptionsList(state),
  selectedProcessOption: selectors.getSelectedProcessOptions(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToProcessOptionsCreate: actions.goToProcessOptionsCreate,
  goToProcessOptionsEdit: actions.goToProcessOptionsEdit,
  goToProcessOptionsView: actions.goToProcessOptionsView,
  selectProcessOptions: actions.selectProcessOptions,
  getProcessOptionsList: actions.getProcessOptionsList,
  deleteProcessOptions: actions.deleteProcessOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const ProcessOptionsList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data = [],
  selectedProcessOption,
  goToProcessOptionsCreate,
  goToProcessOptionsEdit,
  goToProcessOptionsView,
  selectProcessOptions,
  getProcessOptionsList,
  deleteProcessOptions,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<ProcessOption[] | []>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>(processColumnDefs);

  useEffect(() => {
    getProcessOptionsList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getProcessOptionsList]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);
      setDataGrid(clonedData);
    }
  }, [data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => {
    getProcessOptionsList(deliveryFunctionId);
  };

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToProcessOptionsCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectProcessOptions(event.api.getSelectedRows());
  };

  const handleShowActionMenuItem = () => true;

  const handleEdit = () => goToProcessOptionsEdit(history);

  const handleSelectItemMenuAction = () => goToProcessOptionsView(history);

  const handleDelete = () => {
    const distributorConfigrationId = selectedProcessOption[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedProcessOption[0]?.PROCNAME,
        AuditService.PROCESS_OPTIONS,
        AuditActionType.PROCESS_OPTIONS_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteProcessOptions(deliveryFunctionId, distributorConfigrationId, body);
  };

  const hasPrivilegeToView = privileges.includes(ProcessOptionPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(ProcessOptionPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(ProcessOptionPrivileges.Delete);

  return (
    <Fragment>
      <DistributorGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        onShowActionMenuItem={handleShowActionMenuItem}
        lastUpdatedAt={lastUpdatedAt}
        selectedProcessOptions={selectedProcessOption[0]}
        onDeleteRow={handleDelete}
        onEdit={handleEdit}
      />
    </Fragment>
  );
};

export default withRouter(connector(ProcessOptionsList));
